import { FormControl } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Injectable, Signal, computed, effect, signal, untracked } from '@angular/core';
import { Observable, debounce, distinctUntilChanged, filter, map, shareReplay, timer } from 'rxjs';

/**
 * interfaces
 */
import { ITableSortMeta, TableSortMode, TableSortOrderType, ITableColumnsUnion } from '../interfaces';

/**
 * enums
 */
import { TableActionMode } from '../enums';

/**
 * utilities
 */
import { TableUtils } from '../utilities';
import { AppUtils } from '@core/utilities';
import { SharedUtils } from '@shared/utils/shared-utils';

/**
 * @service TableStateService
 * @description Signal-based state management service for table component
 * SSR-safe and optimized for Angular 19+ with signal-based reactivity
 * 
 * @note This service is scoped at component level (not root) so each table instance
 * has its own independent state. Provide it in the component's providers array.
 */
@Injectable()
export class TableStateService {

  /**
   * Signal for value
   */
  private readonly _value = signal<any[]>([]);
  public readonly value = this._value.asReadonly();

  /**
   * Signal for columns
   */
  private readonly _columns = signal<ITableColumnsUnion>([]);
  public readonly columns = this._columns.asReadonly();

  /**
   * Signal for current page
   */
  private readonly _currentPage = signal<number>(1);
  public readonly currentPage = this._currentPage.asReadonly();

  /**
   * Signal for rows per page
   */
  private readonly _rowsPerPage = signal<number>(10);
  public readonly rowsPerPage = this._rowsPerPage.asReadonly();

  /**
   * Signal for total records
   */
  private readonly _totalRecords = signal<number>(0);
  public readonly totalRecords = computed(() => {
    return this._lazy() ? this._totalRecords() : this.filteredData().length;
  });

  /**
   * Loading state signal
   */
  private readonly _loading = signal<boolean>(false);
  public readonly loading = this._loading.asReadonly();

  /**
   * Error state signal
   */
  private readonly _error = signal<string | null>(null);
  public readonly error = this._error.asReadonly();
  
  /**
   * Signal for lazy mode
   */
  private readonly _lazy = signal<boolean>(false);
  public readonly lazy = this._lazy.asReadonly();

  /**
   * Signal for selection key (property name for selection identifier)
   * @default 'id'
   */
  private readonly _selectionKey = signal<any>('id');
  public readonly selectionKey = this._selectionKey.asReadonly();

  /**
   * Signal for selected rows
   */
  private readonly _selectedRows = signal<any[]>([]);
  public readonly selectedRows = this._selectedRows.asReadonly();

  /**
   * Subject for search input changes
   * Emitted by TableSearchComponent when user types
   */
  public readonly searchFormControl = new FormControl<string | null>('');

  /**
   * Computed signal for search value
   */
  public readonly searchValue = toSignal<string>(
    this.searchFormControl.valueChanges.pipe<string>(
      map((value) => String(value ?? ''))
    )
  ) as Signal<string>;

  /**
   * Settled search term, normalized for comparison.
   *
   * @note The debounce delay is resolved per emission (not once at field-init time),
   * because `_lazy` is set by the table component *after* this service is constructed.
   * Reading it eagerly would freeze the delay at the initial `false` value.
   *
   * @note `distinctUntilChanged` runs *after* the debounce so the settled value is
   * compared against the last emitted one — dedup before debouncing only drops
   * consecutive keystrokes, which are always distinct while typing.
   *
   * @note `shareReplay` keeps this a *single* pipeline. Both the signal below and
   * the page-reset subscription in the constructor read from it, so one keystroke
   * settles once and notifies both in the same synchronous emission — two
   * independent subscriptions would each run their own debounce timer and could
   * be split across change-detection passes, firing two lazy loads per search.
   */
  private readonly _searchValue$: Observable<string> = this.searchFormControl.valueChanges.pipe(
    filter((value): value is string => typeof value === 'string'),
    map((value) => value.toLowerCase().trim()),
    debounce(() => timer(this._lazy() ? 500 : 0)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  /**
   * Signal for search value
   */
  public readonly searchValueWithOperatorsPipe = toSignal<string, string>(
    this._searchValue$,
    { initialValue: '' }
  );

  /**
   * Computed signal for filtered data
   * @description Filters data by search value
   */
  public readonly filteredData = computed(() => {
    
    const value = this.value();
    return this._filterDataBySearchValue(value);
  });

  /**
   * Computed signal for sorted data
   * @description Sorts filtered data by sort meta (single or multiple)
   */
  public readonly sortedData = computed(() => {
    return SharedUtils.sortRowsByMeta(this.filteredData(), this.sortMeta());
  });

  /**
   * Computed signal for frazzled state
   * @description Returns true if loading is true or error is not null
   */
  public readonly frazzled = computed(() => {
    return Boolean(this.loading() || this.error() || this.paginatedData().length === 0);
  });

  /**
   * Computed signals for derived state
   *
   * @note Derived from `totalRecords` so the page count always agrees with the
   * count the pager renders: the server-reported *filtered* total in lazy mode,
   * the locally filtered row count otherwise. Reading the unfiltered row count
   * here would leave the page buttons frozen at their pre-search count.
   */
  public readonly totalPages = computed(() => {
    const rows = this._rowsPerPage();
    if (rows <= 0) return 0;
    return Math.ceil(this.totalRecords() / rows);
  });

  /**
   * Computed signal for visible columns (filter out hidden columns)
   */
  public readonly visibleColumns = computed(() => {
    return this.columns().filter(col => !col.hidden);
  });

  /**
   * Computed signal for visible data columns (filter out action columns)
   */
  public readonly visibleDataColumns = computed(() => {
    return this.visibleColumns().filter(col => !TableUtils.isColumnActions(col));
  });  

  /**
   * Computed signal for paginated data
   */
  public readonly paginatedData = computed(() => {
    
    // init
    if (this._lazy()) {
      // server-side pagination - return data as is
      return this._value();
    }

    // client-side pagination - use sorted data
    const page = this.currentPage();
    const rows = this.rowsPerPage();
    const data = this.lazy() ? this.filteredData() : this.sortedData();


    // return paginated data
    return data.slice((page - 1) * rows, page * rows).slice(0, rows);
  });


  /**
   * Current sort rules for the table.
   * In single mode, it contains at most one rule.
   * In multiple mode, rules are ordered by priority.
   */
  private readonly _sortMeta = signal<ITableSortMeta[]>([]);
  public readonly sortMeta = this._sortMeta.asReadonly();

  /**
   * Sorting behavior mode.
   * - 'single': only one sorted column at a time
   * - 'multiple': supports multi-column sorting
   */
  private _sortMode = signal<TableSortMode>('single');
  public readonly sortMode = this._sortMode.asReadonly();

  /**
   * Highest-priority sort rule, or null when nothing is sorted.
   * In multiple mode this is the first rule in `sortMeta`.
   */
  public readonly primarySortMeta = computed<ITableSortMeta | null>(() => {
    return this.sortMeta()[0] ?? null;
  });

  /**
   * Column name of the primary sort rule, resolved for a server-side request.
   *
   * `sortMeta` holds whatever key the header cell was bound to — a view-model
   * `field` such as `endPeriod` — so the column config is consulted for a
   * `sortField` override before the name leaves the table. Empty string when
   * nothing is sorted, matching the API's "unsorted" value.
   *
   * @note Only the primary rule is exposed: the API sorts by a single column,
   * so a `multiple`-mode lazy table would silently drop its secondary rules.
   * Keep lazy tables on the default `single` mode.
   */
  public readonly sortColumn = computed<string>(() => {

    // nothing sorted — let the request fall back to the API default
    const meta = this.primarySortMeta();
    if (meta == null) return '';

    // resolve the API column name from the column config
    const column = this.columns().find(col => col.field === meta.column || col.id === meta.column);

    return column?.sortField ?? meta.column;
  });

  /**
   * Order of the primary sort rule, or null when nothing is sorted.
   *
   * Stays in the table's own `'asc' | 'desc'` vocabulary — translating it to
   * the API's `ASC`/`DESC` is the caller's job, so this module keeps no
   * knowledge of any wire format.
   */
  public readonly sortOrder = computed<TableSortOrderType | null>(() => {
    return this.primarySortMeta()?.order ?? null;
  });

  /**
   * Computed signal for inline actions
   */
  public readonly inlineActions = computed(() => {
    return this.visibleColumns()
                .filter(col => TableUtils.isColumnActions(col))
                .flatMap(col => col.actions)
                .filter(action => action.actionMode === TableActionMode.Inline);
  });

  /**
   * Computed signal for menu actions
   */
  public readonly menuActions = computed(() => {
    return this.visibleColumns()
                .filter(col => TableUtils.isColumnActions(col))
                .flatMap(col => col.actions)
                .filter(action => action.actionMode === TableActionMode.Menu);
  });

  constructor() {

    // A settled search term describes a different result set, so the page the
    // user was on no longer means anything — asking the server for page 3 of a
    // narrower set returns nothing. Reset before the table's lazy-load effect
    // flushes, so the refetch carries both the new term and page 1 at once.
    this._searchValue$
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.setCurrentPage(1));

    // watch totalPages with currentPage to make it updated with any change on page view
    effect(() => {
      const totalPages = this.totalPages();

      // A lazy request in flight has not reported its counts yet; clamping
      // against the previous total would fight the page just requested.
      if (this._loading()) return;

      const currentPage = untracked(() => this.currentPage());

      // `Math.max(…, 1)` keeps page 1 valid for an empty result set, so an
      // as-yet-unloaded table is never reset out from under its caller.
      if (currentPage > Math.max(totalPages, 1)) {
        this.setCurrentPage(1)
      }
    });
  }

  /**
   * Methods to update state
   */
  public setValue(value: any[]): void {
    
    // validate value
    if (!Array.isArray(value)) {
      throw new Error('Value must be an array');
    }

    // set value
    this._value.set(value);
  }

  /**
   * Method to load data into the table (called by parent component after onLazyLoad event)
   * @param data - Array of data to display
   * @param totalRecords - Total number of records (for pagination)
   */
  public loadData(data: any[], totalRecords: number): void {
    this.setValue(data);
    this.setTotalRecords(totalRecords);
  }  

  /**
   * Method to set columns
   */
  public setColumns(columns: ITableColumnsUnion): void {
    
    // validate columns
    if (!Array.isArray(columns)) {
      throw new Error('Columns must be an array');
    }

    // set columns
    this._columns.set(columns);
  }

  /**
   * Method to set sort mode
   */
  public setSortMode(mode: TableSortMode): void {
    this._sortMode.set(mode);
  }

  /**
   * Method to set sort meta directly
   */
  public setSortMeta(meta: ITableSortMeta[]): void {
    if (!Array.isArray(meta)) {
      throw new Error('Sort meta must be an array');
    }

    this._sortMeta.set(meta);
    this.setCurrentPage(1);
  }

  /**
   * Method to clear sorting
   */
  public clearSortMeta(): void {
    this.setSortMeta([]);
    this.setCurrentPage(1);
  }

  /**
   * Method to get sort order by field
   */
  public getSortOrder(field: string): ITableSortMeta['order'] | null {
    if (typeof field !== 'string' || field.trim().length === 0) return null;
    return this.sortMeta().find(meta => meta.column === field)?.order ?? null;
  }

  /**
   * Method to toggle sorting for a field
   * @param field - Column field
   * @param append - In multiple mode, append/update instead of replace
   */
  public toggleSort(field: string, append = false): void {
    const nextMeta = TableUtils.nextSortMeta(
      this.sortMeta(),
      field,
      this.sortMode(),
      append
    );

    this.setSortMeta(nextMeta);
    this.setCurrentPage(1);
  }

  /**
   * Method to set current page
   */
  public setCurrentPage(page: number): void {
    
    // validate page
    if (typeof page !== 'number' || page <= 0) {
      throw new Error('Page must be a positive number');
    }

    // set page
    this._currentPage.set(page);
  }

  /**
   * Method to set rows per page
   */
  public setRowsPerPage(rows: number): void {
    
    // validate rows
    if (typeof rows !== 'number' || rows <= 0) {
      throw new Error('Rows must be a positive number');
    }

    // set rows
    this._rowsPerPage.set(rows);
    // reset to first page when rows per page changes
    this.setCurrentPage(1);
  }

  /**
   * Method to set total records
   */
  public setTotalRecords(total: number): void {
    
    // validate total
    if (typeof total !== 'number' || total < 0) {
      throw new Error('Total must be a positive number');
    }

    // set total
    this._totalRecords.set(total);
  }

  /**
   * Method to set loading state
   */
  public setLoading(loading: boolean): void {
    
    // validate loading
    if (typeof loading !== 'boolean') {
      throw new Error('Loading must be a boolean');
    }

    // set loading
    this._loading.set(loading);
  }

  /**
   * Method to set error state
   */
  public setError(error: any): void {
    
    // validate error
    if (error == null) {
      throw new Error('Error must be a non-null value');
    }
    
    // set error
    this._error.set(error);
  }

  /**
   * Method to set lazy mode
   */
  public setLazy(lazy: boolean): void {
    
    // validate lazy
    if (typeof lazy !== 'boolean') {
      throw new Error('Lazy must be a boolean');
    }

    // set lazy
    this._lazy.set(lazy);
  }

  /**
   * Method to set search value (for backward compatibility)
   */
  public setSearchValue(value: string): void {
    
    // validate value
    if (value == null) {
      throw new Error('Value must be a non-null value');
    }
    
    // set value
    this.searchFormControl.setValue(value);
  }

  /**
   * Method to filter data by search value
   * @param data - Array of data to filter
   * @returns Array of filtered data
   */
  private _filterDataBySearchValue(data: any[]): any[] {
    
    // validate data
    if (!Array.isArray(data)) return [];
    
    // validate search value
    const searchValue = this.searchValueWithOperatorsPipe();
    
    // if search value is empty, return data
    if (searchValue.trim().length === 0) {
      return data;
    }
    
    const cols = this.visibleDataColumns().filter(col => col?.searchBy != undefined);
    const filteredData = data.filter(row => {
      return cols.some(col => {

        // helper function to get search field value
        const fieldValue = AppUtils.chainByReducerFn<any, any>(col.searchBy!, row);
        
        // validate field value
        if (fieldValue == null) return false;

        return String(fieldValue).toLowerCase().includes(searchValue);
      });
    });

    return filteredData;

  }

  /**
   * Method to set selection key
   */
  public setSelectionKey(key: unknown): void {
    
    // validate key
    if (key === undefined || key === null) {
      throw new Error('Key must be a non-null value');
    }

    // set key
    this._selectionKey.set(key);
  }

  /**
   * Method to select a row
   */
  public selectRow(row: any): void {
    
    // validate row
    if (!row) {
      throw new Error('Row must be a non-null value');
    }
    
    // check if row is already selected
    if (this.isRowSelected(row)) {
      return; // Row already selected, no need to add again
    }
    
    // set selected rows
    this._selectedRows.update(prev => [...prev, row]);
  }

  /**
   * 
   */
  public unselectRow(row: any): void {
    
    // validate row
    if (!row) {
      throw new Error('Row must be a non-null value');
    }
    
    // set selected rows
    this._selectedRows.update(prev => prev.filter(r => r[this._selectionKey()] !== row[this._selectionKey()]));

  }

  /**
   * Method to toggle a row selection
   */
  public onRowToggle(row: any, selected: unknown): void {

    const isSelected = selected as boolean;

    try {
      if (isSelected) {
        this.selectRow(row);
      } else {
        this.unselectRow(row);
      }
    } 
    catch (error) {
      throw error;
    }

  }

  /**
   * Method to handle toggle all rows
   */
  public onToggleAllRows(rows: any[], selected: unknown): void {

    const isSelected = selected as boolean;

    try {
      if (isSelected) {
        this.setSelectedRows(rows);
      } else {
        this.removeSelectedRows(rows);
      }
    } 
    catch (error) {
      throw error;
    }
  }  

  /**
   * Method to set selected rows (adds to existing selection)
   * Use replaceSelectedRows() if you want to replace the entire selection
   */
  public setSelectedRows(rows: any[]): void {
    
    // validate rows
    if (!Array.isArray(rows)) {
      throw new Error('Rows must be an array');
    }
    
    const key = this._selectionKey();
    const currentSelectedRows = this._selectedRows();
    
    // Filter out rows that are already selected
    const newRows = rows.filter(row => 
      !currentSelectedRows.some(selectedRow => selectedRow?.[key] === row?.[key])
    );

    // Add only new rows to existing selection
    if (newRows.length > 0) {
      this._selectedRows.update(prev => [...prev, ...newRows]);
    }
    
  }

  /**
   * Method to replace all selected rows (clears existing and sets new selection)
   */
  public replaceSelectedRows(rows: any[]): void {
    
    // validate rows
    if (!Array.isArray(rows)) {
      throw new Error('Rows must be an array');
    }
    
    // Replace entire selection
    this._selectedRows.set([...rows]);
  }

  /**
   * Method to remove selected rows by key comparison
   */
  public removeSelectedRows(rows: any[]): void {
    
    // validate rows
    if (!Array.isArray(rows)) {
      throw new Error('Rows must be an array');
    }
    
    const key = this._selectionKey();
    
    // Remove rows by comparing keys instead of object references
    this._selectedRows.update(prev => 
      prev.filter(selectedRow => 
        !rows.some(row => row?.[key] === selectedRow?.[key])
      )
    );
  }

  /**
   * Method to clear selected rows
   */
  public clearSelectedRows(): void {
    this._selectedRows.set([]);
  }

  /**
   * Method to check if a row is selected
   */
  public isRowSelected(row: any): boolean {
    
    // validate row
    if (!row) {
      throw new Error('Row must be a non-null value');
    }
    
    // check if row is selected
    return this._selectedRows().findIndex(r => r[this._selectionKey()] === row[this._selectionKey()]) !== -1;
  }

  /**
   * Method to check if all current page rows are selected
   */
  public isAllCurrentPageRowsSelected(): boolean {
    const paginatedData = this.paginatedData();
    if (paginatedData.length === 0) return false;
    
    const key = this._selectionKey();
    const selectedRows = this._selectedRows();
    
    return paginatedData.every(row => 
      selectedRows.some(selectedRow => selectedRow?.[key] === row?.[key])
    );
  }

  /**
   * Method to check if any current page rows are selected
   */
  public isAnyCurrentPageRowsSelected(): boolean {
    const paginatedData = this.paginatedData();
    if (paginatedData.length === 0) return false;
    
    const key = this._selectionKey();
    const selectedRows = this._selectedRows();
    
    return paginatedData.some(row => 
      selectedRows.some(selectedRow => selectedRow?.[key] === row?.[key])
    );
  }

}
